/**
 * Settings → Integrations → Bing Mail — /admin/settings/bing-mail
 * Connection health, TEST/LIVE mode, customer-hold toggle, address
 * window layout (mm). Credentials are backend-only, never displayed.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mailbox, ShieldAlert, CheckCircle2, XCircle, Plug } from "lucide-react";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";

const card = "rounded-2xl bg-white border border-slate-200 p-5";

export default function AdminBingMailSettings() {
  const [data, setData] = useState(null);
  const [layout, setLayout] = useState({});
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/integrations/bing-mail").then(({ data: d }) => {
      setData(d); setLayout(d.layout);
    }).catch(() => toast.error("Failed to load Bing Mail settings"));
  }, []);
  useEffect(load, [load]);

  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      const { data: d } = await api.patch("/admin/integrations/bing-mail", body);
      setData(d); setLayout(d.layout); toast.success(okMsg);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  const changeMode = (mode) => {
    if (mode === "live" && !window.confirm(
      "Switch Bing Mail to LIVE mode?\n\nApproved mailouts will create real Bing packs. With 'hold for release' ON they still wait in the Bing Customer Portal for manual release; with it OFF they print and post automatically. Continue?")) return;
    patch({ mode }, `Bing Mail mode set to ${mode.toUpperCase()}`);
  };

  const testConnection = async () => {
    setBusy(true); setTestResult(null);
    try {
      const { data: r } = await api.post("/admin/integrations/bing-mail/test-connection");
      setTestResult({ ok: true, msg: `Connected — session ${r.epid_sample} issued for account ${r.account}` });
      load();
    } catch (e) {
      setTestResult({ ok: false, msg: e.response?.data?.detail || "Connection failed" });
    } finally { setBusy(false); }
  };

  if (!data) return <PortalShell><div className="p-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div></PortalShell>;
  const live = data.mode === "live";

  return (
    <PortalShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="bing-settings-page">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Settings · Integrations</div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 mt-2 flex items-center gap-2"><Mailbox className="w-7 h-7 text-cyan-700" /> Bing Mail (EasyPost)</h1>
        <p className="text-sm text-slate-500 mt-2">Prints and posts Property Campaign letters via Bing Mail Australia. Credentials live in the server environment only and are never displayed.</p>

        <div className={`mt-5 rounded-xl px-4 py-3 flex items-center gap-3 border ${live ? "bg-rose-50 border-rose-300" : "bg-amber-50 border-amber-300"}`} data-testid="bing-mode-banner">
          <ShieldAlert className={`w-5 h-5 ${live ? "text-rose-600" : "text-amber-600"}`} />
          <div className="text-sm">
            <b className={live ? "text-rose-700" : "text-amber-800"}>
              {live ? "LIVE MODE — approved mailouts create real Bing packs" : "TEST MODE — every submission is customer-held at Bing and must be manually released from the Bing Customer Portal"}
            </b>
            {data.mode_changed_by && <div className="text-xs text-slate-500 mt-0.5">Mode set by {data.mode_changed_by}</div>}
          </div>
          <div className="ml-auto flex gap-2">
            <Button data-testid="bing-set-test" size="sm" variant={live ? "outline" : "default"} className={!live ? "bg-amber-600 hover:bg-amber-700" : ""} disabled={busy || !live} onClick={() => changeMode("test")}>Test mode</Button>
            <Button data-testid="bing-set-live" size="sm" variant={live ? "default" : "outline"} className={live ? "bg-rose-600 hover:bg-rose-700" : ""} disabled={busy || live || !data.configured} onClick={() => changeMode("live")}>Live mode</Button>
          </div>
        </div>

        <div className={`${card} mt-5`} data-testid="bing-connection-card">
          <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-3 flex items-center gap-1.5"><Plug className="w-4 h-4" /> Connection</h2>
          <div className="flex items-center gap-2 text-sm" data-testid="bing-config-status">
            {data.configured ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-rose-500" />}
            {data.configured
              ? <>Credentials configured (account {data.account_id})</>
              : <>Credentials missing — set BING_MAIL_ACCOUNT_ID / BING_MAIL_USERNAME / BING_MAIL_PASSWORD in the server environment (use a dedicated API user, not a personal portal login)</>}
            <Button size="sm" variant="outline" className="ml-auto" disabled={busy || !data.configured} onClick={testConnection} data-testid="bing-test-connection">Test connection</Button>
          </div>
          {testResult && (
            <div data-testid="bing-test-result" className={`mt-3 text-sm font-semibold ${testResult.ok ? "text-emerald-700" : "text-rose-600"}`}>{testResult.msg}</div>
          )}
          <div className="mt-3 text-xs text-slate-500 space-y-0.5">
            <div data-testid="bing-last-success">Last successful API interaction: <b>{data.last_success_at ? new Date(data.last_success_at).toLocaleString("en-AU") : "—"}</b></div>
            {data.last_error && <div data-testid="bing-last-error" className="text-rose-600">Latest API error ({data.last_error_at ? new Date(data.last_error_at).toLocaleString("en-AU") : ""}): {data.last_error}</div>}
          </div>
        </div>

        <div className={`${card} mt-5`} data-testid="bing-options-card">
          <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-3">Production &amp; Safety</h2>
          <div className="text-sm text-slate-600">Letters print <b>A4, full colour, single sided, Australia Post Regular</b> (stored with each mailout's audit record).</div>
          <label className="mt-3 flex items-start gap-2 text-sm">
            <input type="checkbox" data-testid="bing-cust-hold-live" className="mt-0.5" checked={!!data.cust_hold_live} disabled={busy}
              onChange={(e) => patch({ cust_hold_live: e.target.checked }, "Hold setting saved")} />
            <span><b>Hold LIVE mailings for manual release</b> — live packs wait in the Bing Customer Portal until you release them. Recommended until you've reviewed the first physical output. (Test mode is always held.)</span>
          </label>

          <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider mt-6 mb-2">Address Window Position</h2>
          <p className="text-xs text-slate-500 mb-3">The letter's address block position for Bing envelopes. Adjust after reviewing a held Bing test mailing — PostGrid's positions may not match Bing's window.</p>
          <div className="flex flex-wrap gap-4 items-end">
            <label className="text-sm">From left (mm)
              <input data-testid="bing-layout-left" type="number" min="5" max="120" step="0.5" value={layout.address_left_mm ?? ""} onChange={(e) => setLayout({ ...layout, address_left_mm: e.target.value })}
                className="mt-1 block w-24 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </label>
            <label className="text-sm">From top (mm)
              <input data-testid="bing-layout-top" type="number" min="5" max="120" step="0.5" value={layout.address_top_mm ?? ""} onChange={(e) => setLayout({ ...layout, address_top_mm: e.target.value })}
                className="mt-1 block w-24 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
            </label>
            <Button data-testid="bing-save-layout" disabled={busy} onClick={() => patch({ layout }, "Address layout saved")} className="rounded-full bg-slate-900">Save layout</Button>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}
